import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  Button,
  ContentBox,
  ContentPage,
  DropdownAction,
  FullTable,
  NoResults,
  Notice,
  PageActions,
  TableFilters,
  Text,
} from "@ficatranquilo/contabilidade.com-ui";
import useService from "@hooks/useService";
import useListFilters from "@hooks/useListFilters";
import { DomainStatus, escapeHtml, formatDateTime, usePageHeader } from "@components/ow";
import { questionsLabel, quizStatusKey, respondentsLabel } from "@helpers/quizForm";
import DeleteQuizModal from "./DeleteQuizModal";

// Questionários da empresa (modelo Lista do DS OneWork), só do ADM. O clique na linha
// abre o editar; "Ver respostas", a tela das respostas.

// Uma das opções do seletor da FullTable (10, 25, 50, 100)
const PAGE_SIZE = 25;

const LIST_HREF = "/c/quiz";
const BREADCRUMB = [{ page: "Questionários", route: LIST_HREF }];

const STATUS_DATA = [
  { label: "Todos", value: "" },
  { label: "Ativos", value: "ATIVO" },
  { label: "Inativos", value: "INATIVO" },
];
const INITIAL_FILTERS = { status: STATUS_DATA[0] };

const EMPTY = { totalRegisters: 0, list: [], totalPages: 1 };

const NoticeSpace = styled.div`
  margin-bottom: 20px;
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

// Título longo corta com reticências; o inteiro fica no title
const Title = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export default function Quiz() {
  const quizService = useService("quiz");
  const navigate = useNavigate();

  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: PAGE_SIZE });
  const [paginationData, setPaginationData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  // Só a resposta da última requisição vale (filtros seguidos podem voltar fora de ordem)
  const lastRequest = useRef(0);

  const { storeFilters, filters, filter, handleSetFilters, handleClearFilters, hasFilter } = useListFilters(
    INITIAL_FILTERS,
    setPagination
  );

  usePageHeader({ title: "Questionários", breadcrumb: BREADCRUMB });

  const searchTerm = (filters.search || "").trim();
  const statusValue = filters.status?.value || "";

  useEffect(() => {
    fetchQuizzes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.pageIndex, pagination.pageSize, searchTerm, statusValue]);

  function buildQuery() {
    const params = [`page=${pagination.pageIndex + 1}`, `limit=${pagination.pageSize}`];
    if (searchTerm) params.push(`title=${encodeURIComponent(searchTerm)}`);
    if (statusValue) params.push(`status=${statusValue}`);
    return params.join("&");
  }

  async function fetchQuizzes() {
    const request = ++lastRequest.current;
    setLoading(true);
    try {
      const result = await quizService.list(buildQuery());
      if (request !== lastRequest.current) return;
      if (!result?.data?.list) throw new Error(typeof result === "string" ? result : "Erro ao carregar questionários");
      // Excluir o último da última página deixaria a página vazia
      if (!result.data.list.length && pagination.pageIndex > 0) {
        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
        return;
      }
      setPaginationData(result.data);
    } catch (error) {
      if (request !== lastRequest.current) return;
      toast.error(error?.response?.data?.msg || error?.message || "Erro ao carregar questionários");
      setPaginationData(EMPTY);
    }
    setLoading(false);
  }

  const openNew = () => navigate(`${LIST_HREF}/new`);
  const openEdit = (quiz) => navigate(`${LIST_HREF}/${quiz.id}`);
  const openAnswers = (quiz) => navigate(`${LIST_HREF}/${quiz.id}/answers`);

  async function toggleStatus(quiz) {
    const activate = quizStatusKey(quiz.status) === "inativo";
    // Em erro, o service já mostra o toast com a mensagem do back
    const result = await quizService.updateStatus(quiz.id, activate ? "ATIVO" : "INATIVO");
    if (!result || result.error) return;
    toast.success(
      activate
        ? "Questionário ativado. Os colaboradores já podem responder."
        : "Questionário desativado. Os colaboradores deixam de vê-lo."
    );
    fetchQuizzes();
  }

  function afterDelete() {
    setDeleting(null);
    fetchQuizzes();
  }

  const columns = [
    {
      id: "title",
      header: "Questionário",
      size: 420,
      cell: ({ row }) => {
        const quiz = row.original;
        return (
          <Stack>
            <Title title={quiz.title}>
              <Text $text={escapeHtml(quiz.title || "Questionário")} $variant="paragraph_small_bold" $color="N900" />
            </Title>
            <Text $text={questionsLabel(quiz.totalQuestions)} $variant="caption_medium" $color="N400" />
          </Stack>
        );
      },
    },
    {
      id: "respondents",
      header: "Respostas",
      size: 150,
      cell: ({ row }) => {
        const count = Number(row.original.totalRespondents) || 0;
        return <Text $text={respondentsLabel(count)} $variant="paragraph_small_medium" $color={count ? "N900" : "N400"} />;
      },
    },
    {
      id: "status",
      header: "Status",
      size: 130,
      cell: ({ row }) => <DomainStatus domain="questionario" value={quizStatusKey(row.original.status)} />,
    },
    // Os criados antes do 7.3a não têm data ("—"): o back não a gravava
    { id: "created_at", header: "Criado em", size: 180, cell: ({ row }) => formatDateTime(row.original.created_at) },
    {
      id: "actions",
      // O "Ações" da lib tem 70px; abaixo disso a seta sai da célula
      size: 80,
      minSize: 80,
      maxSize: 80,
      align: "right",
      cell: ({ row }) => {
        const quiz = row.original;
        const active = quizStatusKey(quiz.status) === "ativo";
        return (
          <DropdownAction
            options={[
              { label: "Editar", action: () => openEdit(quiz) },
              { label: "Ver respostas", action: () => openAnswers(quiz) },
              { label: active ? "Desativar" : "Ativar", action: () => toggleStatus(quiz) },
              { label: "Excluir", action: () => setDeleting(quiz), isImportant: true },
            ]}
          />
        );
      },
    },
  ];

  const filtering = !!hasFilter || !!searchTerm || !!statusValue;
  const noResults = filtering ? (
    <NoResults notResultText="Nenhum questionário encontrado" includeText="Limpar filtros" includeAction={clearAll} />
  ) : (
    <NoResults notResultText="Nenhum questionário por aqui" includeText="Novo questionário" includeAction={openNew} />
  );

  // "Limpar filtros" do vazio também volta as pílulas para "Todos"
  function clearAll() {
    handleClearFilters();
    storeFilters.setFilters({ status: STATUS_DATA[0] });
  }

  return (
    <ContentPage>
      <PageActions>
        <Button $text="Novo questionário" $variant="primary" $action={openNew} />
      </PageActions>

      <ContentBox>
        <NoticeSpace>
          <Notice
            $variant="gray"
            $text="Os questionários ativos aparecem para todos os colaboradores da empresa, em Questionários."
          />
        </NoticeSpace>

        <TableFilters
          storeFilters={storeFilters}
          searchTerm="search"
          searchPlaceholder="Buscar pelo título"
          statusData={STATUS_DATA}
          filter={filter}
          handleSetFilters={handleSetFilters}
          handleClearFilters={handleClearFilters}
          setStatePagination={setPagination}
          disableFilter
          disableExport
          hasFilter={hasFilter}
        />

        <FullTable
          data={paginationData.list}
          columns={columns}
          rowCount={paginationData.totalRegisters}
          pagination={pagination}
          setPagination={setPagination}
          getRowId={(row) => String(row.id)}
          isLoading={loading}
          onRowClick={openEdit}
          noResults={noResults}
        />
      </ContentBox>

      {deleting ? <DeleteQuizModal quiz={deleting} onClose={() => setDeleting(null)} onDeleted={afterDelete} /> : null}
    </ContentPage>
  );
}
